import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import {
  fieldHasContent,
  transformProperties,
  groupFieldHasContent,
} from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { tabletBreakpoint, fromDesktopBreakpoint } from '@/src/domains/shared/breakpoints';

export const ToneStyle = css`
  ${({ $tone }) => {
    switch ($tone) {
      case 'orange':
        return css`
          background-color: #ff817b;
        `;
      case 'red':
        return css`
          background-color: #e03d5c;
        `;
      case 'green':
        return css`
          background-color: #00b568;
        `;
      default:
        return css`
          background-color: var(--newBrandColor);
        `;
    }
  }}
`;

const Wrapper = styled.section`
  ${fluidProp(['padding-bottom', 'padding-top'], '50px', '75px')};
  text-align: center;
  color: var(--colors-blank);
  margin-top: -100px;
  z-index: 1;
  position: relative;
  ${ToneStyle};

  ${fromDesktopBreakpoint(css`
    clip-path: ellipse(100% 100% at 50% 100%);
    ${fluidProp('padding-top', '90px', '155px')};
  `)}
`;

const ContentSection = styled.div`
  width: 70%;
  margin: 0 auto;

  ${tabletBreakpoint(css`
    width: 100%;
  `)}
`;

export const Title = styled.div`
  ${({ theme }) => theme.typography.scale4};
  margin-bottom: 13px;
  font-family: var(--typography-fontBossa);

  & > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    margin: inherit;
  }
`;

export const Description = styled.div`
  ${({ theme }) => theme.typography.bodyLead};
  font-family: var(--typography-fontBrand);
`;

const IconsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 83px;
  margin-top: 96px;

  ${tabletBreakpoint(css`
    gap: 50px;
  `)}
`;

export const CtaButton = styled(Button)`
  && {
    background-color: var(--colors-blank);
    color: var(--colors-darkBlue);
    font-family: var(--typography-fontBrand);
    text-transform: uppercase;
    ${({ theme }) => theme.typography.bodySMMedium};
    margin-top: 55px;
  }
`;

export function PartnersSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const { title, description, ctaLabel, ctaLink } = primary;

  const { context } = slice;
  const { tone } = context;

  return (
    <Wrapper $tone={tone}>
      <ContentContainer>
        <ContentSection>
          {fieldHasContent(title) && (
            <Title>
              <PrismicRichText field={title} />
            </Title>
          )}
          {fieldHasContent(description) && (
            <Description>
              <PrismicRichText field={description} />
            </Description>
          )}
        </ContentSection>
        {groupFieldHasContent(fields, 'logo') && (
          <IconsWrapper>
            {fields.map(({ logo }, index) => (
              <div key={index}>
                <Image
                  src={logo?.url}
                  alt={logo?.alt || ''}
                  width={logo?.dimensions.width}
                  height={logo?.dimensions.height}
                  quality="100"
                />
              </div>
            ))}
          </IconsWrapper>
        )}
        {ctaLink && fieldHasContent(ctaLabel) && (
          <div>
            <CtaButton href={ctaLink?.url} target={ctaLink?.target}>
              {ctaLabel}
            </CtaButton>
          </div>
        )}
      </ContentContainer>
    </Wrapper>
  );
}
